import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  massOps, firmware, provisionTemplates,
  type MassOp, type MassOpKind, type MassOpStatus,
  type MassOpFilter, type MassOpCreatePayload, type MassOpProgress,
  type FirmwareRelease,
} from '@/lib/api';
import { useTenants } from '@/hooks/useActiveTenant';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import {
  Plus, Power, RefreshCw, FileUp, Settings2, Pause, Play, Square,
  AlertTriangle, Bird, ChevronRight, Trash2,
  type LucideIcon,
} from 'lucide-react';

// ─── Constants ───────────────────────────────────────────────────────────────

const KIND_LABEL: Record<MassOpKind, string> = {
  firmware_upgrade: 'Firmware upgrade',
  set_params:       'Set parameters',
  reboot:           'Reboot',
  apply_template:   'Apply template',
};

const KIND_ICON: Record<MassOpKind, LucideIcon> = {
  firmware_upgrade: FileUp,
  set_params:       Settings2,
  reboot:           Power,
  apply_template:   RefreshCw,
};

const STATUS_BADGE: Record<MassOpStatus, string> = {
  draft:     'badge',
  scheduled: 'badge-info',
  running:   'badge-brand',
  paused:    'badge-warn',
  completed: 'badge-up',
  aborted:   'badge-down',
};

const TIMEZONES = [
  'America/Toronto',
  'America/New_York',
  'America/Vancouver',
  'America/Los_Angeles',
  'America/Chicago',
  'UTC',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function toLocalDatetime(iso?: string | null): string {
  // Convert ISO to <input type="datetime-local"> value (YYYY-MM-DDTHH:MM)
  const d = iso ? new Date(iso) : new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

function fromLocalDatetime(local: string): string {
  // Convert <input type="datetime-local"> value to ISO UTC
  return new Date(local).toISOString();
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function MassOps() {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed'>('all');

  const queryParams = useMemo(() => {
    const p: Record<string, unknown> = { per_page: 50 };
    if (statusFilter === 'completed') p.status = 'completed';
    return p;
  }, [statusFilter]);

  const { data } = useQuery({
    queryKey: ['mass-ops', queryParams],
    queryFn: () => massOps.list(queryParams),
    refetchInterval: 5000,
  });

  const rows = useMemo(() => {
    const list = data?.data ?? [];
    if (statusFilter === 'active') {
      return list.filter((o) => ['scheduled', 'running', 'paused', 'draft'].includes(o.status));
    }
    return list;
  }, [data, statusFilter]);

  return (
    <>
      <PageHeader
        title="Mass operations"
        subtitle={`${rows.length} op${rows.length > 1 ? 's' : ''}`}
        actions={
          <button className="btn btn-primary btn-sm" onClick={() => setWizardOpen(true)}>
            <Plus size={11} />Nouvelle op
          </button>
        }
      />

      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        {(['all', 'active', 'completed'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : ''}`}
          >
            {s === 'all' ? 'Tous' : s === 'active' ? 'Actifs' : 'Terminés'}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto">
        <table className="luc-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Nom</th>
              <th>Progress</th>
              <th>Schedule</th>
              <th>Canary</th>
              <th>Créée</th>
              <th>Par</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((op) => (
              <OpRow key={op.id} op={op} />
            ))}
            {!rows.length && (
              <tr><td colSpan={8} className="text-center py-12 text-text-faint">Aucune mass operation.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateOpWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function OpRow({ op }: { op: MassOp }) {
  const Icon = KIND_ICON[op.kind] ?? Power;
  const sched = `${fmtDate(op.start_at)} → ${fmtDate(op.end_at)}, ${op.daily_from_h}-${op.daily_to_h}h`;

  return (
    <tr>
      <td>
        <span className={`badge ${STATUS_BADGE[op.status]}`}>{op.status}</span>
        {op.behind_schedule && (
          <span className="badge badge-warn ml-1" title="Behind schedule">
            <AlertTriangle size={9} />
          </span>
        )}
      </td>
      <td>
        <Link to={`/app/mass-ops/${op.id}`} className="flex items-center gap-2 font-medium hover:text-brand">
          <Icon size={12} />
          <span>{op.name}</span>
        </Link>
        <div className="text-xs text-text-faint font-mono mt-0.5">{KIND_LABEL[op.kind]}</div>
      </td>
      <td><ProgressBar progress={op.progress} /></td>
      <td className="text-xs font-mono text-text-muted whitespace-nowrap">{sched}</td>
      <td>
        {op.canary_count ? (
          <span className="badge badge-info" title={`Canary status: ${op.canary_status ?? 'pending'}`}>
            <Bird size={9} />{op.canary_count}
            {op.canary_status === 'passed' && <span className="ml-1">passed</span>}
            {op.canary_status === 'failed' && <span className="ml-1 text-danger">failed</span>}
          </span>
        ) : (
          <span className="text-text-faint">—</span>
        )}
      </td>
      <td className="text-xs font-mono text-text-faint whitespace-nowrap">{new Date(op.created_at).toLocaleDateString()}</td>
      <td className="text-xs text-text-muted">{op.creator?.email ?? '—'}</td>
      <td className="text-right"><RowActions op={op} /></td>
    </tr>
  );
}

function ProgressBar({ progress }: { progress?: MassOpProgress }) {
  if (!progress || progress.total === 0) return <span className="text-text-faint text-xs">—</span>;
  const { total, success, failed, running, queued } = progress;
  const done = success + failed;
  const pct = Math.round(100 * done / total);
  const failPct = Math.round(100 * failed / total);

  return (
    <div className="flex items-center gap-2">
      <div className="w-28 h-1.5 bg-bg-2 rounded overflow-hidden flex">
        <div className="h-full bg-success" style={{ width: `${pct - failPct}%` }} />
        <div className="h-full bg-danger" style={{ width: `${failPct}%` }} />
      </div>
      <span className="text-xs text-text-muted font-mono">{done}/{total}</span>
      {failed > 0 && <span className="badge badge-down">{failed}</span>}
      {(running > 0 || queued > 0) && <span className="badge badge-info">{running + queued} run</span>}
    </div>
  );
}

function RowActions({ op }: { op: MassOp }) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['mass-ops'] });

  const arm    = useMutation({ mutationFn: () => massOps.arm(op.id),    onSuccess: invalidate });
  const pause  = useMutation({ mutationFn: () => massOps.pause(op.id),  onSuccess: invalidate });
  const resume = useMutation({ mutationFn: () => massOps.resume(op.id), onSuccess: invalidate });
  const abort  = useMutation({ mutationFn: () => massOps.abort(op.id),  onSuccess: invalidate });

  return (
    <div className="flex items-center gap-1 justify-end">
      {op.status === 'draft' && (
        <button onClick={() => { if (confirm(`Armer "${op.name}" ? Cela matérialisera les targets et passera en scheduled.`)) arm.mutate(); }}
          disabled={arm.isPending} className="btn btn-sm btn-primary">
          <Play size={9} />Arm
        </button>
      )}
      {(op.status === 'running' || op.status === 'scheduled') && (
        <button onClick={() => pause.mutate()} disabled={pause.isPending} className="btn btn-sm">
          <Pause size={9} />Pause
        </button>
      )}
      {op.status === 'paused' && (
        <button onClick={() => resume.mutate()} disabled={resume.isPending} className="btn btn-sm btn-primary">
          <Play size={9} />Resume
        </button>
      )}
      {['scheduled', 'running', 'paused'].includes(op.status) && (
        <button onClick={() => { if (confirm(`Abort "${op.name}" ? Les targets pending seront marqués skipped.`)) abort.mutate(); }}
          disabled={abort.isPending} className="btn btn-sm text-danger">
          <Square size={9} />Abort
        </button>
      )}
      <Link to={`/app/mass-ops/${op.id}`} className="btn btn-sm btn-ghost">
        <ChevronRight size={11} />
      </Link>
    </div>
  );
}

// ─── Wizard ──────────────────────────────────────────────────────────────────

interface WizardState {
  // Step 1
  kind: MassOpKind;
  name: string;
  payload_json: Record<string, unknown>;
  paramRows: Array<{ path: string; value: string }>;
  firmwareId: number | null;
  templateId: number | null;
  // Step 2
  tenant_id?: number;
  filter: MassOpFilter;
  macListRaw: string;
  wanIpListRaw?: string;
  onlineMode: 'any' | 'only-online' | 'only-offline';
  // Step 3
  start_at: string;
  end_at: string;
  daily_from_h: number;
  daily_to_h: number;
  timezone: string;
  max_concurrent: number;
  exclude_weekends: boolean;
  // Step 4
  canaryEnabled: boolean;
  canary_count: number;
  canary_wait_minutes: number;
  canary_failure_threshold_pct: number;
}

function defaultState(): WizardState {
  const now = new Date();
  const start = new Date(now.getTime() + 60 * 60_000); // +1h
  const end = new Date(start.getTime() + 7 * 24 * 60 * 60_000); // +7d
  return {
    kind: 'reboot',
    name: '',
    payload_json: {},
    paramRows: [{ path: '', value: '' }],
    firmwareId: null,
    templateId: null,
    filter: {},
    macListRaw: '',
    wanIpListRaw: '',
    onlineMode: 'any',
    start_at: toLocalDatetime(start.toISOString()),
    end_at: toLocalDatetime(end.toISOString()),
    daily_from_h: 0,
    daily_to_h: 4,
    timezone: 'America/Toronto',
    max_concurrent: 5,
    exclude_weekends: false,
    canaryEnabled: false,
    canary_count: 5,
    canary_wait_minutes: 60,
    canary_failure_threshold_pct: 20,
  };
}

function CreateOpWizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSuperadmin = user?.is_superadmin ?? false;
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [s, setS] = useState<WizardState>(defaultState);

  useEffect(() => {
    if (open) { setStep(1); setS(defaultState()); }
  }, [open]);

  // Build effective filter (mac_in + wan_ip_in + online + bootstrap_state)
  const effectiveFilter: MassOpFilter = useMemo(() => {
    const f: MassOpFilter = { ...s.filter };
    if (s.macListRaw.trim()) {
      f.mac_in = s.macListRaw
        .split(/[\s,;]+/)
        .map((m) => m.trim())
        .filter(Boolean);
    } else {
      delete f.mac_in;
    }
    if (s.wanIpListRaw && s.wanIpListRaw.trim()) {
      (f as Record<string, unknown>).wan_ip_in = s.wanIpListRaw
        .split(/[\s,;]+/)
        .map((m) => m.trim())
        .filter(Boolean);
    } else {
      delete (f as Record<string, unknown>).wan_ip_in;
    }
    if (s.onlineMode === 'only-online') f.online = true;
    else if (s.onlineMode === 'only-offline') f.online = false;
    else delete f.online;
    if (isSuperadmin && s.tenant_id) f.tenant_id = s.tenant_id;
    return f;
  }, [s.filter, s.macListRaw, s.wanIpListRaw, s.onlineMode, s.tenant_id, isSuperadmin]);

  // Build payload from kind-specific fields
  function buildPayload(): Record<string, unknown> {
    if (s.kind === 'firmware_upgrade') {
      return s.firmwareId ? { firmware_id: s.firmwareId } : {};
    }
    if (s.kind === 'apply_template') {
      return s.templateId ? { template_id: s.templateId } : {};
    }
    if (s.kind === 'set_params') {
      const params: Record<string, string> = {};
      s.paramRows.forEach((r) => { if (r.path) params[r.path] = r.value; });
      return { params };
    }
    return {};
  }

  function buildBody(): MassOpCreatePayload {
    const body: MassOpCreatePayload = {
      name: s.name,
      kind: s.kind,
      filter_json: effectiveFilter,
      payload_json: buildPayload(),
      start_at: fromLocalDatetime(s.start_at),
      end_at: fromLocalDatetime(s.end_at),
      daily_from_h: s.daily_from_h,
      daily_to_h: s.daily_to_h,
      timezone: s.timezone,
      max_concurrent: s.max_concurrent,
      exclude_weekends: s.exclude_weekends,
    };
    if (isSuperadmin && s.tenant_id) body.tenant_id = s.tenant_id;
    if (s.canaryEnabled) {
      body.canary_count = s.canary_count;
      body.canary_wait_minutes = s.canary_wait_minutes;
      body.canary_failure_threshold_pct = s.canary_failure_threshold_pct;
    }
    return body;
  }

  const createDraft = useMutation({
    mutationFn: () => massOps.create(buildBody()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mass-ops'] });
      onClose();
    },
    onError: (e: Error & { response?: { data?: unknown } }) => {
      alert('Erreur création : ' + (JSON.stringify(e.response?.data) ?? e.message));
    },
  });

  const createAndArm = useMutation({
    mutationFn: async () => {
      const op = await massOps.create(buildBody());
      await massOps.arm(op.id);
      return op;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mass-ops'] });
      onClose();
    },
    onError: (e: Error & { response?: { data?: unknown } }) => {
      alert('Erreur création/arm : ' + (JSON.stringify(e.response?.data) ?? e.message));
    },
  });

  const canNext1 = !!s.name && (
    s.kind === 'reboot' ||
    (s.kind === 'firmware_upgrade' && s.firmwareId) ||
    (s.kind === 'apply_template'   && s.templateId) ||
    (s.kind === 'set_params'       && s.paramRows.some((r) => r.path && r.value))
  );
  const canNext3 = s.start_at && s.end_at && s.daily_to_h > s.daily_from_h && new Date(s.end_at) > new Date(s.start_at);

  return (
    <Modal open={open} onClose={onClose} title="Nouvelle mass operation" width="max-w-3xl">
      <div className="flex items-center gap-2 mb-5 text-xs">
        {[1, 2, 3, 4].map((n) => {
          const labels = ['1 · Action', '2 · Cible', '3 · Schedule', '4 · Canary'];
          return (
            <div key={n} className={`flex-1 py-2 px-3 rounded text-center font-medium ${step === n ? 'bg-brand-dim text-brand' : 'bg-bg-2 text-text-muted'}`}>
              {labels[n - 1]}
            </div>
          );
        })}
      </div>

      {step === 1 && <Step1 s={s} setS={setS} />}
      {step === 2 && <Step2 s={s} setS={setS} effectiveFilter={effectiveFilter} isSuperadmin={isSuperadmin} />}
      {step === 3 && <Step3 s={s} setS={setS} />}
      {step === 4 && <Step4 s={s} setS={setS} />}

      <div className="flex justify-between mt-6 pt-4 border-t border-border">
        <button onClick={() => step > 1 && setStep((step - 1) as 1 | 2 | 3 | 4)} disabled={step === 1} className="btn">
          ← Retour
        </button>
        {step < 4 ? (
          <button
            onClick={() => setStep((step + 1) as 1 | 2 | 3 | 4)}
            disabled={(step === 1 && !canNext1) || (step === 3 && !canNext3)}
            className="btn btn-primary"
          >
            Suivant →
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => createDraft.mutate()} disabled={createDraft.isPending || createAndArm.isPending} className="btn">
              {createDraft.isPending ? 'Création…' : 'Save as draft'}
            </button>
            <button onClick={() => createAndArm.mutate()} disabled={createDraft.isPending || createAndArm.isPending} className="btn btn-primary">
              {createAndArm.isPending ? 'Arming…' : 'Save & arm'}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Step 1 — Action type ────────────────────────────────────────────────────

function Step1({ s, setS }: { s: WizardState; setS: React.Dispatch<React.SetStateAction<WizardState>> }) {
  const kinds: Array<{ value: MassOpKind; label: string; desc: string; icon: LucideIcon }> = [
    { value: 'firmware_upgrade', label: 'Firmware upgrade', desc: 'Push une release sur la fleet',         icon: FileUp },
    { value: 'set_params',       label: 'Set params',       desc: 'Modifier des paramètres TR-069',        icon: Settings2 },
    { value: 'reboot',           label: 'Reboot',           desc: 'Redémarrer les devices',                icon: Power },
    { value: 'apply_template',   label: 'Apply template',   desc: 'Appliquer un provision template',        icon: RefreshCw },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {kinds.map(({ value, label, desc, icon: Icon }) => (
          <button
            key={value}
            onClick={() => setS({ ...s, kind: value })}
            className={`card card-pad flex items-center gap-3 text-left cursor-pointer ${s.kind === value ? 'border-brand bg-brand/5' : ''}`}
          >
            <div className="w-9 h-9 rounded bg-surface-hi flex items-center justify-center text-text-muted">
              <Icon size={16} />
            </div>
            <div>
              <div className="font-medium text-sm">{label}</div>
              <div className="text-text-faint text-xs">{desc}</div>
            </div>
          </button>
        ))}
      </div>

      <label className="block">
        <span className="text-xs text-text-muted block mb-1">Nom de l'opération *</span>
        <input className="input w-full" value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })}
          placeholder={`${KIND_LABEL[s.kind]} ${new Date().toLocaleDateString()}`} />
      </label>

      {s.kind === 'firmware_upgrade' && <FirmwarePicker s={s} setS={setS} />}
      {s.kind === 'apply_template'   && <TemplatePicker s={s} setS={setS} />}
      {s.kind === 'set_params'       && <ParamsEditor s={s} setS={setS} />}
      {s.kind === 'reboot'           && <p className="text-xs text-text-faint">Aucun payload requis.</p>}
    </div>
  );
}

function FirmwarePicker({ s, setS }: { s: WizardState; setS: React.Dispatch<React.SetStateAction<WizardState>> }) {
  const { data: list } = useQuery({
    queryKey: ['firmware-releases', s.filter.manufacturer],
    queryFn: () => firmware.list({ vendor: s.filter.manufacturer }),
  });
  return (
    <label className="block">
      <span className="text-xs text-text-muted block mb-1">Firmware release *</span>
      <select className="input w-full" value={s.firmwareId ?? ''}
        onChange={(e) => setS({ ...s, firmwareId: e.target.value ? Number(e.target.value) : null })}>
        <option value="">— Choisir —</option>
        {(list ?? []).map((fw: FirmwareRelease) => (
          <option key={fw.id} value={fw.id}>{fw.vendor} {fw.model} v{fw.version}</option>
        ))}
      </select>
      <span className="text-xs text-text-faint mt-1 block">
        Filtré par vendor (Step 2 → manufacturer). {(list?.length ?? 0)} release(s) disponible(s).
      </span>
    </label>
  );
}

function TemplatePicker({ s, setS }: { s: WizardState; setS: React.Dispatch<React.SetStateAction<WizardState>> }) {
  const { data } = useQuery({
    queryKey: ['provision-templates'],
    queryFn: () => provisionTemplates.list({ per_page: 200 }),
  });
  return (
    <label className="block">
      <span className="text-xs text-text-muted block mb-1">Provision template *</span>
      <select className="input w-full" value={s.templateId ?? ''}
        onChange={(e) => setS({ ...s, templateId: e.target.value ? Number(e.target.value) : null })}>
        <option value="">— Choisir —</option>
        {(data?.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
    </label>
  );
}

function ParamsEditor({ s, setS }: { s: WizardState; setS: React.Dispatch<React.SetStateAction<WizardState>> }) {
  function update(i: number, field: 'path' | 'value', v: string) {
    const rows = [...s.paramRows];
    rows[i] = { ...rows[i], [field]: v };
    setS({ ...s, paramRows: rows });
  }
  function add()        { setS({ ...s, paramRows: [...s.paramRows, { path: '', value: '' }] }); }
  function remove(i: number) { setS({ ...s, paramRows: s.paramRows.filter((_, idx) => idx !== i) }); }

  return (
    <div>
      <span className="text-xs text-text-muted block mb-2">Paramètres TR-069 *</span>
      <div className="space-y-1.5">
        {s.paramRows.map((row, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input className="input flex-1 font-mono text-xs" placeholder="InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.SSID"
              value={row.path} onChange={(e) => update(i, 'path', e.target.value)} />
            <input className="input w-40 font-mono text-xs" placeholder="Valeur"
              value={row.value} onChange={(e) => update(i, 'value', e.target.value)} />
            <button onClick={() => remove(i)} className="btn btn-sm btn-ghost text-danger" disabled={s.paramRows.length === 1}>
              <Trash2 size={11} />
            </button>
          </div>
        ))}
      </div>
      <button onClick={add} className="btn btn-sm mt-2"><Plus size={10} />Add param</button>
    </div>
  );
}

// ─── Step 2 — Filter targets ─────────────────────────────────────────────────

function Step2({ s, setS, effectiveFilter, isSuperadmin }: {
  s: WizardState; setS: React.Dispatch<React.SetStateAction<WizardState>>;
  effectiveFilter: MassOpFilter; isSuperadmin: boolean;
}) {
  const { data: tenants } = useTenants();

  // Debounced preview
  const [debounced, setDebounced] = useState(effectiveFilter);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(effectiveFilter), 500);
    return () => clearTimeout(t);
  }, [effectiveFilter]);

  const { data: preview, isFetching } = useQuery({
    queryKey: ['mass-ops-preview', debounced, s.tenant_id],
    queryFn: () => massOps.preview(debounced, isSuperadmin ? s.tenant_id : undefined),
  });

  function updateFilter<K extends keyof MassOpFilter>(k: K, v: MassOpFilter[K] | undefined) {
    setS({ ...s, filter: { ...s.filter, [k]: v } });
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3">
        {isSuperadmin && (
          <label className="block">
            <span className="text-xs text-text-muted block mb-1">Tenant</span>
            <select className="input w-full" value={s.tenant_id ?? ''}
              onChange={(e) => setS({ ...s, tenant_id: e.target.value ? Number(e.target.value) : undefined })}>
              <option value="">— Mon tenant —</option>
              {(tenants ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
        )}

        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Manufacturer</span>
          <select className="input w-full" value={s.filter.manufacturer ?? ''}
            onChange={(e) => updateFilter('manufacturer', e.target.value || undefined)}>
            <option value="">— Tous —</option>
            <option value="Calix">Calix</option>
            <option value="SmartRG">SmartRG</option>
            <option value="Adtran">Adtran</option>
            <option value="Zyxel">Zyxel</option>
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Firmware &lt;</span>
          <input className="input w-full font-mono text-xs" placeholder="12.2.12"
            value={s.filter.firmware_lt ?? ''}
            onChange={(e) => updateFilter('firmware_lt', e.target.value || undefined)} />
        </label>

        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Firmware &gt;</span>
          <input className="input w-full font-mono text-xs" placeholder="11.0.0"
            value={s.filter.firmware_gt ?? ''}
            onChange={(e) => updateFilter('firmware_gt', e.target.value || undefined)} />
        </label>

        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Tag</span>
          <input className="input w-full" placeholder="canary, priority…"
            value={s.filter.tag ?? ''}
            onChange={(e) => updateFilter('tag', e.target.value || undefined)} />
        </label>

        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Bootstrap state</span>
          <select className="input w-full" value={s.filter.bootstrap_state ?? ''}
            onChange={(e) => updateFilter('bootstrap_state', (e.target.value || undefined) as MassOpFilter['bootstrap_state'])}>
            <option value="">— Any —</option>
            <option value="adopted">Adopted</option>
            <option value="provisioned">Provisioned</option>
            <option value="unknown">Unknown</option>
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Online filter</span>
          <select className="input w-full" value={s.onlineMode}
            onChange={(e) => setS({ ...s, onlineMode: e.target.value as WizardState['onlineMode'] })}>
            <option value="any">Any</option>
            <option value="only-online">Only online</option>
            <option value="only-offline">Only offline</option>
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-text-muted block mb-1">
            MAC list <span className="text-text-faint">(one per line, wildcard <code className="text-brand">*</code> OK)</span>
          </span>
          <textarea className="input w-full font-mono text-xs" rows={4}
            placeholder="AA:BB:CC:DD:EE:FF&#10;E82C6D0A*  ← prefix match&#10;*FF50      ← suffix match"
            value={s.macListRaw}
            onChange={(e) => setS({ ...s, macListRaw: e.target.value })} />
        </label>

        <label className="block">
          <span className="text-xs text-text-muted block mb-1">
            WAN IP list <span className="text-text-faint">(wildcard <code className="text-brand">*</code> OK, public ou privée)</span>
          </span>
          <textarea className="input w-full font-mono text-xs" rows={4}
            placeholder="23.252.227.*&#10;192.168.1.100&#10;199.4.190.*"
            value={s.wanIpListRaw ?? ''}
            onChange={(e) => setS({ ...s, wanIpListRaw: e.target.value })} />
        </label>
      </div>

      {/* Preview */}
      <div className="card card-pad bg-bg-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-text-muted">Devices ciblés</span>
          <span className="text-sm font-mono">
            {isFetching ? <span className="text-text-faint">…</span> : <strong className="text-brand">{preview?.count ?? 0}</strong>}
          </span>
        </div>
        {preview && preview.sample.length > 0 && (
          <div className="overflow-auto max-h-40">
            <table className="luc-table text-xs">
              <thead>
                <tr><th>Serial</th><th>MAC</th><th>Firmware</th><th>Online</th></tr>
              </thead>
              <tbody>
                {preview.sample.map((d) => (
                  <tr key={d.id}>
                    <td className="font-mono">{d.serial_number}</td>
                    <td className="font-mono text-text-muted">{d.mac_address ?? '—'}</td>
                    <td className="font-mono text-text-muted">{d.firmware_version ?? '—'}</td>
                    <td>{d.is_online ? <span className="dot dot-up" /> : <span className="dot dot-down" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.count > preview.sample.length && (
              <div className="text-xs text-text-faint mt-1">… +{preview.count - preview.sample.length} autres</div>
            )}
          </div>
        )}
        {preview && preview.count === 0 && (
          <div className="text-xs text-text-faint text-center py-3">Aucun device ne correspond aux filtres.</div>
        )}
      </div>
    </div>
  );
}

// ─── Step 3 — Schedule ───────────────────────────────────────────────────────

function Step3({ s, setS }: { s: WizardState; setS: React.Dispatch<React.SetStateAction<WizardState>> }) {
  const hours = s.daily_to_h - s.daily_from_h;
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Start at *</span>
          <input type="datetime-local" className="input w-full" value={s.start_at}
            onChange={(e) => setS({ ...s, start_at: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-xs text-text-muted block mb-1">End at *</span>
          <input type="datetime-local" className="input w-full" value={s.end_at}
            onChange={(e) => setS({ ...s, end_at: e.target.value })} />
        </label>
      </div>

      <div>
        <span className="text-xs text-text-muted block mb-1">Daily window</span>
        <div className="flex items-center gap-3">
          <input type="number" min={0} max={23} className="input w-20" value={s.daily_from_h}
            onChange={(e) => setS({ ...s, daily_from_h: parseInt(e.target.value, 10) })} />
          <span className="text-text-faint">→</span>
          <input type="number" min={1} max={24} className="input w-20" value={s.daily_to_h}
            onChange={(e) => setS({ ...s, daily_to_h: parseInt(e.target.value, 10) })} />
          <span className={`text-xs ${hours <= 0 ? 'text-danger' : 'text-text-muted'} font-mono`}>
            {s.daily_from_h.toString().padStart(2, '0')}:00 → {s.daily_to_h.toString().padStart(2, '0')}:00 = {hours}h/day
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Timezone</span>
          <select className="input w-full" value={s.timezone} onChange={(e) => setS({ ...s, timezone: e.target.value })}>
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-text-muted block mb-1">Max concurrent (1-100)</span>
          <input type="number" min={1} max={100} className="input w-full" value={s.max_concurrent}
            onChange={(e) => setS({ ...s, max_concurrent: parseInt(e.target.value, 10) })} />
        </label>
      </div>

      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={s.exclude_weekends}
          onChange={(e) => setS({ ...s, exclude_weekends: e.target.checked })} />
        <span className="text-sm">Exclude weekends</span>
      </label>
    </div>
  );
}

// ─── Step 4 — Canary + Recap ─────────────────────────────────────────────────

function Step4({ s, setS }: { s: WizardState; setS: React.Dispatch<React.SetStateAction<WizardState>> }) {
  return (
    <div className="space-y-4 text-sm">
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={s.canaryEnabled}
          onChange={(e) => setS({ ...s, canaryEnabled: e.target.checked })} />
        <span className="font-medium">Enable canary deployment</span>
        <Bird size={14} className="text-brand" />
      </label>

      {s.canaryEnabled && (
        <div className="card card-pad space-y-3 bg-bg-2">
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs text-text-muted block mb-1">Canary count</span>
              <input type="number" min={1} className="input w-full" value={s.canary_count}
                onChange={(e) => setS({ ...s, canary_count: parseInt(e.target.value, 10) })} />
            </label>
            <label className="block">
              <span className="text-xs text-text-muted block mb-1">Wait minutes</span>
              <input type="number" min={5} className="input w-full" value={s.canary_wait_minutes}
                onChange={(e) => setS({ ...s, canary_wait_minutes: parseInt(e.target.value, 10) })} />
            </label>
            <label className="block">
              <span className="text-xs text-text-muted block mb-1">Failure threshold %</span>
              <input type="number" min={1} max={100} className="input w-full" value={s.canary_failure_threshold_pct}
                onChange={(e) => setS({ ...s, canary_failure_threshold_pct: parseInt(e.target.value, 10) })} />
            </label>
          </div>
          <p className="text-xs text-text-faint">
            The first <strong className="text-text-muted">{s.canary_count}</strong> devices will run first,
            then wait <strong className="text-text-muted">{s.canary_wait_minutes} min</strong>.
            If more than <strong className="text-text-muted">{s.canary_failure_threshold_pct}%</strong> fail,
            the rest is auto-aborted.
          </p>
        </div>
      )}

      {/* Recap */}
      <div className="card card-pad bg-bg-2">
        <h3 className="text-xs uppercase tracking-wider text-text-faint mb-2 font-medium">Recap</h3>
        <dl className="text-xs space-y-1 font-mono">
          <Recap k="Kind"     v={KIND_LABEL[s.kind]} />
          <Recap k="Name"     v={s.name || '(auto)'} />
          <Recap k="Filter"   v={JSON.stringify(s.filter) === '{}' && !s.macListRaw && s.onlineMode === 'any' ? 'tous' : 'voir step 2'} />
          <Recap k="Schedule" v={`${new Date(s.start_at).toLocaleString()} → ${new Date(s.end_at).toLocaleString()}`} />
          <Recap k="Window"   v={`${s.daily_from_h}h–${s.daily_to_h}h ${s.timezone}${s.exclude_weekends ? ', no weekends' : ''}`} />
          <Recap k="Concur."  v={`${s.max_concurrent}`} />
          <Recap k="Canary"   v={s.canaryEnabled ? `${s.canary_count} devices, wait ${s.canary_wait_minutes}min, threshold ${s.canary_failure_threshold_pct}%` : 'disabled'} />
        </dl>
      </div>
    </div>
  );
}

function Recap({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex">
      <dt className="w-20 text-text-faint">{k}</dt>
      <dd className="flex-1">{v}</dd>
    </div>
  );
}

